import { useMemo, useState } from 'react'
import { Button, SegmentControl, TextInput } from '@forwardflow/ui-v2'
import {
   IConversationDefinition,
   IEmailItem,
   IPersonaDefinition,
   IPhoneDefinition,
   IPhoneSection,
   ISmsItem,
   IStrategyDefinition,
   IVoicemailItem,
   TContentDefinition,
   TContentKind,
} from '../../interfaces/campaign-v2.types'
import { ListCard, Pill, TextArea, TextButton } from '../shared'

type TChannel = 'sms' | 'email' | 'voicemail'

const CHANNEL_LABEL: Record<TChannel, string> = {
   sms: 'Texts',
   email: 'Emails',
   voicemail: 'Voicemails',
}

/**
 * One editor for every content kind. An outbound content group shows all three
 * channels, each an ordered series the user can add to, remove from and
 * reorder; the server derives order from array position. Protected phone
 * sections stay read-only. Names are generated, shown small, editable.
 */
export const ContentEditor = ({
   kind,
   initial,
   shared,
   title,
   lede,
   onSave,
   onDraftChange,
   onCancel,
   saving,
   slotDirection,
}: {
   kind: TContentKind
   initial: TContentDefinition
   /** The item being edited is a shared template (saving makes a copy). */
   shared: boolean
   title: string
   lede?: string
   onDraftChange?: (def: TContentDefinition) => void
   onSave: (def: TContentDefinition) => void
   onCancel: () => void
   saving?: boolean
   /** Phone scripts: which way the call goes; fixed by the slot. */
   slotDirection?: 'outbound' | 'inbound'
}) => {
   const [def, setLocalDef] = useState<TContentDefinition>(() =>
      JSON.parse(JSON.stringify(initial))
   )
   const [renaming, setRenaming] = useState(false)
   const setDef = (next: TContentDefinition) => {
      setLocalDef(next)
      onDraftChange?.(next)
   }
   const valid = useMemo(() => validate(kind, def), [kind, def])

   return (
      <div className="space-y-4" data-testid="content-editor">
         <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
               <p className="text-white font-semibold text-[15px]">{title}</p>
               {lede && <p className="text-[#99A1AF] text-xs mt-0.5">{lede}</p>}
               {shared && (
                  <p
                     className="text-xs text-amber-300/80 mt-1"
                     data-testid="shared-note"
                  >
                     Saving creates a copy for this campaign’s draft. The
                     current library item stays unchanged.
                  </p>
               )}
            </div>
            <TextButton onClick={onCancel} tone="muted">
               Cancel
            </TextButton>
         </div>

         {renaming ? (
            <TextInput
               size="xs"
               label="Name"
               value={def.name}
               onChange={(e) => setDef({ ...def, name: e.currentTarget.value })}
               maxLength={200}
               data-testid="content-name"
            />
         ) : (
            <div className="flex items-center gap-3 text-xs text-white/50">
               <span className="min-w-0 truncate">{def.name}</span>
               <TextButton onClick={() => setRenaming(true)} tone="muted">
                  Rename
               </TextButton>
            </div>
         )}

         {kind === 'strategy' && (
            <StrategyFields
               def={def as IStrategyDefinition}
               onChange={(d) => setDef(d)}
            />
         )}
         {kind === 'phone' && (
            <PhoneFields
               def={def as IPhoneDefinition}
               onChange={(d) => setDef(d)}
               direction={slotDirection}
            />
         )}
         {kind === 'conversation' && (
            <TextArea
               label="How to reply to a lead's message"
               hint="Guidance for answering replies. This is not the next scheduled text."
               rows={6}
               value={(def as IConversationDefinition).content}
               onChange={(v) =>
                  setDef({ ...(def as IConversationDefinition), content: v })
               }
               testId="conversation-content"
            />
         )}
         {kind === 'persona' && (
            <PersonaFields
               def={def as IPersonaDefinition}
               onChange={(d) => setDef(d)}
            />
         )}

         <div className="flex items-center justify-between gap-3 pt-1">
            <p className="text-xs text-white/45">{valid || 'Looks good.'}</p>
            <Button
               size="middle"
               className="!h-10"
               onClick={() => onSave(def)}
               disabled={Boolean(valid)}
               loading={saving}
               data-testid="content-save"
            >
               {shared ? 'Save my copy' : 'Save'}
            </Button>
         </div>
      </div>
   )
}

const validate = (
   kind: TContentKind,
   def: TContentDefinition
): string | null => {
   if (!def.name?.trim()) return 'Give it a name.'
   if (kind === 'strategy') {
      const s = def as IStrategyDefinition
      const filled =
         (s.sms || []).filter((i) => i.template.trim()).length +
         (s.email || []).filter((i) => i.subject.trim() || i.body.trim())
            .length +
         (s.voicemail || []).filter((i) => i.template.trim()).length
      if (!filled) return 'Write at least one text, email or voicemail.'
      if ((s.sms || []).some((i) => !i.template.trim()))
         return 'Every text needs words.'
      if ((s.email || []).some((i) => !i.subject.trim() || !i.body.trim()))
         return 'Every email needs a subject and a body.'
      if ((s.voicemail || []).some((i) => !i.template.trim()))
         return 'Every voicemail needs words.'
      if ([s.sms, s.email, s.voicemail].some((l) => (l || []).length > 100))
         return 'At most 100 items per channel.'
      return null
   }
   if (kind === 'phone') {
      const p = def as IPhoneDefinition
      if (!p.firstMessage.trim()) return 'Write the opening line.'
      if (!p.sections.length) return 'Add at least one section.'
      if (p.sections.some((s) => !s.name.trim() || !s.content.trim()))
         return 'Every section needs a name and instructions.'
      return null
   }
   if (kind === 'conversation')
      return (def as IConversationDefinition).content.trim()
         ? null
         : 'Write how to reply.'
   if (kind === 'persona') {
      const g = (def as IPersonaDefinition).guidance
      return [
         g.personaGuidance,
         g.voiceStyle,
         g.speakingGuidance,
         g.conversationStyle,
      ].some((v) => v && v.trim())
         ? null
         : 'Describe how the assistant should sound.'
   }
   return null
}

/* ---------- Outbound content group: three ordered series ---------- */

const StrategyFields = ({
   def,
   onChange,
}: {
   def: IStrategyDefinition
   onChange: (d: IStrategyDefinition) => void
}) => {
   const [channel, setChannel] = useState<TChannel>('sms')
   const counts = {
      sms: def.sms?.length || 0,
      email: def.email?.length || 0,
      voicemail: def.voicemail?.length || 0,
   }

   const list = (def[channel] || []) as Array<
      ISmsItem | IEmailItem | IVoicemailItem
   >
   const setList = (next: Array<ISmsItem | IEmailItem | IVoicemailItem>) =>
      onChange({ ...def, [channel]: next })
   const move = (i: number, dir: -1 | 1) => {
      const j = i + dir
      if (j < 0 || j >= list.length) return
      const next = [...list]
      ;[next[i], next[j]] = [next[j], next[i]]
      setList(next)
   }
   const add = () =>
      setList([
         ...list,
         channel === 'email'
            ? { subject: '', body: '', contentType: 'text' }
            : { template: '' },
      ])

   return (
      <div className="space-y-3">
         <SegmentControl
            value={channel}
            onChange={(v) => setChannel(v as TChannel)}
            data={(['sms', 'email', 'voicemail'] as TChannel[]).map((c) => ({
               value: c,
               label: `${CHANNEL_LABEL[c]} · ${counts[c]}`,
            }))}
            data-testid="channel-tabs"
         />
         <ListCard
            hint={
               channel === 'voicemail'
                  ? 'Left when a call with "leave a voicemail" goes unanswered. Each unanswered call uses the next one.'
                  : channel === 'email'
                    ? 'Sent by the email touches in your cadence, in this order.'
                    : 'Sent by the text touches in your cadence, in this order.'
            }
            testId={`series-${channel}`}
         >
            {!list.length && (
               <p className="px-4 py-4 text-sm text-white/45">
                  No {CHANNEL_LABEL[channel].toLowerCase()} yet. Add content if
                  your cadence uses this channel.
               </p>
            )}
            {list.map((item, i) => (
               <div
                  key={i}
                  className="px-4 py-3 space-y-2"
                  data-testid={`series-${channel}-${i}`}
               >
                  <div className="flex items-center gap-2">
                     <Pill tone="purple">
                        {CHANNEL_LABEL[channel].replace(/s$/, '')} {i + 1}
                     </Pill>
                     {'id' in item && item.id !== undefined && (
                        <Pill>saved</Pill>
                     )}
                     <span className="flex-1" />
                     <TextButton
                        onClick={() => move(i, -1)}
                        tone="muted"
                        disabled={i === 0}
                     >
                        Up
                     </TextButton>
                     <TextButton
                        onClick={() => move(i, 1)}
                        tone="muted"
                        disabled={i === list.length - 1}
                     >
                        Down
                     </TextButton>
                     <TextButton
                        onClick={() => setList(list.filter((_, j) => j !== i))}
                        tone="danger"
                        testId={`series-${channel}-${i}-remove`}
                     >
                        Remove
                     </TextButton>
                  </div>
                  {channel === 'email' ? (
                     <>
                        <TextInput
                           size="sm"
                           placeholder="Subject"
                           value={(item as IEmailItem).subject}
                           onChange={(e) =>
                              setList(
                                 list.map((it, j) =>
                                    j === i
                                       ? {
                                            ...it,
                                            subject: e.currentTarget.value,
                                         }
                                       : it
                                 )
                              )
                           }
                           data-testid={`series-${channel}-${i}-subject`}
                        />
                        <TextArea
                           rows={4}
                           placeholder="Body"
                           value={(item as IEmailItem).body}
                           onChange={(v) =>
                              setList(
                                 list.map((it, j) =>
                                    j === i ? { ...it, body: v } : it
                                 )
                              )
                           }
                           testId={`series-${channel}-${i}-body`}
                        />
                     </>
                  ) : (
                     <TextArea
                        rows={channel === 'sms' ? 2 : 3}
                        placeholder={
                           channel === 'sms'
                              ? 'Hi {{first_name}}, …'
                              : 'Hi, this is … please call us back when convenient.'
                        }
                        value={(item as ISmsItem).template}
                        onChange={(v) =>
                           setList(
                              list.map((it, j) =>
                                 j === i ? { ...it, template: v } : it
                              )
                           )
                        }
                        testId={`series-${channel}-${i}-text`}
                     />
                  )}
               </div>
            ))}
            <button
               type="button"
               onClick={add}
               className="w-full flex items-center gap-3 px-4 h-11 text-left text-sm font-medium text-[#A684FF] hover:bg-[#9F7BFF]/[0.07]"
               data-testid={`series-${channel}-add`}
            >
               <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#9F7BFF]/15 text-[#C9B8FF] text-sm leading-none">
                  +
               </span>
               Add{' '}
               {channel === 'sms'
                  ? 'a text'
                  : channel === 'email'
                    ? 'an email'
                    : 'a voicemail'}
            </button>
         </ListCard>
         <p className="text-xs text-white/40">
            Placeholders like {'{{first_name}}'} and {'{{assistant_name}}'} are
            filled in at send time. Keep the ones already in a template.
         </p>
      </div>
   )
}

/* ---------- Phone script ---------- */

const PhoneFields = ({
   def,
   onChange,
   direction,
}: {
   def: IPhoneDefinition
   onChange: (d: IPhoneDefinition) => void
   direction?: 'outbound' | 'inbound'
}) => {
   const inbound = (direction || def.callType) === 'inbound'
   const sectionType: IPhoneSection['type'] = inbound
      ? 'call_inbound'
      : 'call_outbound'
   const setSection = (i: number, patch: Partial<IPhoneSection>) =>
      onChange({
         ...def,
         sections: def.sections.map((s, j) =>
            j === i ? { ...s, ...patch } : s
         ),
      })
   return (
      <div className="space-y-3">
         <TextArea
            label="Opening line"
            hint={
               inbound
                  ? 'How the assistant answers the call.'
                  : 'The first thing the assistant says when the lead picks up.'
            }
            rows={2}
            value={def.firstMessage}
            onChange={(v) => onChange({ ...def, firstMessage: v })}
            testId="phone-first-message"
         />
         <ListCard
            title="Sections"
            hint="What the assistant should do, in order. Protected sections come from the template and cannot be changed."
            testId="phone-sections"
         >
            {def.sections.map((s, i) => (
               <div
                  key={String(s.sectionId ?? i)}
                  className="px-4 py-3 space-y-2"
                  data-testid={`section-${i}`}
               >
                  <div className="flex items-center gap-2">
                     <TextInput
                        size="xs"
                        className="flex-1"
                        value={s.name}
                        onChange={(e) =>
                           setSection(i, { name: e.currentTarget.value })
                        }
                        disabled={s.protected}
                        placeholder="Section name"
                     />
                     {s.protected && <Pill tone="warn">protected</Pill>}
                     {!s.protected && (
                        <TextButton
                           onClick={() =>
                              onChange({
                                 ...def,
                                 sections: def.sections.filter(
                                    (_, j) => j !== i
                                 ),
                              })
                           }
                           tone="danger"
                        >
                           Remove
                        </TextButton>
                     )}
                  </div>
                  <TextArea
                     rows={3}
                     value={s.content}
                     onChange={(v) => setSection(i, { content: v })}
                     disabled={s.protected}
                     placeholder="Instructions for this part of the call"
                     testId={`section-${i}-content`}
                  />
               </div>
            ))}
            <button
               type="button"
               onClick={() =>
                  onChange({
                     ...def,
                     sections: [
                        ...def.sections,
                        {
                           name: '',
                           type: sectionType,
                           order: def.sections.length + 1,
                           content: '',
                        },
                     ],
                  })
               }
               className="w-full flex items-center gap-3 px-4 h-11 text-left text-sm font-medium text-[#A684FF] hover:bg-[#9F7BFF]/[0.07]"
               data-testid="section-add"
            >
               <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#9F7BFF]/15 text-[#C9B8FF] text-sm leading-none">
                  +
               </span>
               Add a section
            </button>
         </ListCard>
      </div>
   )
}

/* ---------- Persona ---------- */

const PersonaFields = ({
   def,
   onChange,
}: {
   def: IPersonaDefinition
   onChange: (d: IPersonaDefinition) => void
}) => {
   const g = def.guidance
   const set = (k: keyof IPersonaDefinition['guidance'], v: string) =>
      onChange({ ...def, guidance: { ...g, [k]: v || null } })
   return (
      <div className="space-y-3">
         <TextArea
            label="Personality"
            rows={3}
            value={g.personaGuidance || ''}
            onChange={(v) => set('personaGuidance', v)}
            placeholder="Warm, direct, never pushy."
            testId="persona-personality"
         />
         <details className="text-sm text-white/60">
            <summary className="cursor-pointer">
               Additional guidance (optional)
            </summary>
            <div className="space-y-3 mt-3">
               <TextArea
                  label="Voice style"
                  rows={3}
                  value={g.voiceStyle || ''}
                  onChange={(v) => set('voiceStyle', v)}
                  placeholder="Calm and unhurried."
               />
               <TextArea
                  label="How it speaks"
                  rows={3}
                  value={g.speakingGuidance || ''}
                  onChange={(v) => set('speakingGuidance', v)}
                  placeholder="Short natural sentences."
               />
               <TextArea
                  label="How it converses"
                  rows={3}
                  value={g.conversationStyle || ''}
                  onChange={(v) => set('conversationStyle', v)}
                  placeholder="One question at a time."
               />
            </div>
         </details>
      </div>
   )
}
